import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Star, Gift, PartyPopper } from 'lucide-react';

const icons = {
  success: CheckCircle,
  error: XCircle,
  points: Star,
  gift: Gift,
  party: PartyPopper,
};

const colors = {
  success: '#10B981',
  error: '#EF4444',
  points: '#D4AF37',
  gift: '#D4AF37',
  party: '#D4AF37',
};

export function ResultOverlay({ show, type = 'success', title, subtitle, onClose, autoClose = 3000 }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const Icon = icons[type] || icons.success;
  const color = colors[type] || colors.success;

  useEffect(() => {
    if (show) {
      setVisible(true);
      setExiting(false);
      if (autoClose > 0) {
        const timer = setTimeout(() => {
          setExiting(true);
          setTimeout(() => { setVisible(false); onClose?.(); }, 500);
        }, autoClose);
        return () => clearTimeout(timer);
      }
    }
  }, [show, autoClose, onClose]);

  if (!visible) return null;

  return (
    <div className={`fixed inset-0 z-[100] flex items-center justify-center transition-all duration-500 ${exiting ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
      style={{ background: 'rgba(5,5,5,0.92)', backdropFilter: 'blur(20px)' }}
      onClick={() => { setExiting(true); setTimeout(() => { setVisible(false); onClose?.(); }, 500); }}>
      <div className="text-center px-6 max-w-sm" style={{ animation: exiting ? 'none' : 'overlayBounce 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }}>
        <div style={{ animation: exiting ? 'none' : 'overlayIcon 0.8s cubic-bezier(0.16, 1, 0.3, 1)' }}>
          <Icon className="w-20 h-20 mx-auto mb-6" style={{ color, filter: `drop-shadow(0 0 30px ${color}50)` }} />
        </div>
        <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Outfit', color }}>{title}</h2>
        {subtitle && <p className="text-base text-[#A1A1AA]">{subtitle}</p>}
        {type === 'points' && (
          <div className="mt-4 flex justify-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5" style={{ color, animation: `overlayStars 0.4s ease ${0.1 * i}s both`, opacity: 0 }} fill={color} />
            ))}
          </div>
        )}
        {type === 'party' && (
          <div className="mt-4 text-3xl" style={{ animation: 'overlayParty 1s ease infinite' }}>
            {"🎉"}
          </div>
        )}
        <p className="text-xs text-[#A1A1AA] mt-6">{"Touchez pour fermer"}</p>
      </div>
      <style>{`
        @keyframes overlayBounce {
          0% { opacity: 0; transform: scale(0.5) translateY(30px); }
          60% { transform: scale(1.05) translateY(-5px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes overlayIcon {
          0% { transform: scale(0) rotate(-20deg); }
          60% { transform: scale(1.2) rotate(5deg); }
          100% { transform: scale(1) rotate(0); }
        }
        @keyframes overlayStars {
          0% { opacity: 0; transform: scale(0) translateY(10px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes overlayParty {
          0%, 100% { transform: scale(1) rotate(0); }
          25% { transform: scale(1.2) rotate(-10deg); }
          75% { transform: scale(1.2) rotate(10deg); }
        }
      `}</style>
    </div>
  );
}
